import * as Sentry from '@sentry/vue'

/** Cap event volume if a device thrash-heals (should be rare after force-set). */
export const STALE_PRESENTATION_PEAK_HEAL_SENTRY_COOLDOWN_MS = 15 * 60 * 1000

export const STALE_PRESENTATION_PEAK_HEAL_MESSAGE
  = 'Player healed stale presentation peak (wrap poison)'

export type TStalePresentationPeakHealReport = {
  beforePeakIdx: number
  afterIdx: number
  planLen: number
  fallbackIdx: number
  frameId: string | null
}

let lastIssueCapturedAtMs = 0

/**
 * Rising-edge Sentry issue when we force-heal a monotonic peak ≥ planLen (#1662).
 * Filter: message {@link STALE_PRESENTATION_PEAK_HEAL_MESSAGE}, tag
 * `player_stale_presentation_peak_heal:true` / `player_diagnostics_fault:stale_presentation_peak`.
 */
export function reportStalePresentationPeakHealToSentry(
  report: TStalePresentationPeakHealReport
): void {
  const nowMs = Date.now()
  const data = {
    beforePeakIdx: report.beforePeakIdx,
    afterIdx: report.afterIdx,
    planLen: report.planLen,
    fallbackIdx: report.fallbackIdx,
    frameId: report.frameId ?? undefined
  }

  try {
    Sentry.addBreadcrumb({
      category: 'player_diagnostics_fault',
      message: 'player_diagnostics_fault_stale_presentation_peak',
      level: 'warning',
      data
    })
  }
  catch {
    /* Sentry not initialized or unavailable */
  }

  if (
    lastIssueCapturedAtMs > 0
    && nowMs - lastIssueCapturedAtMs < STALE_PRESENTATION_PEAK_HEAL_SENTRY_COOLDOWN_MS
  ) {
    return
  }
  lastIssueCapturedAtMs = nowMs

  try {
    Sentry.captureMessage(STALE_PRESENTATION_PEAK_HEAL_MESSAGE, {
      level: 'error',
      fingerprint: ['player-stale-presentation-peak-heal'],
      tags: {
        player_stale_presentation_peak_heal: 'true',
        player_diagnostics_fault: 'stale_presentation_peak',
        stale_presentation_peak: 'true'
      },
      extra: data
    })
  }
  catch {
    /* Sentry not initialized or unavailable */
  }
}

/** Test-only reset of dedupe state. */
export function resetStalePresentationPeakHealSentryDedupeForTests(): void {
  lastIssueCapturedAtMs = 0
}
